import dayjs, { type Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';

/** Date only, e.g. "Mar 4, 2026". */
export const DATE_FORMAT = 'MMM D, YYYY';
/** Date and time, e.g. "Mar 4, 2026 14:05". */
export const DATE_TIME_FORMAT = 'MMM D, YYYY HH:mm';
/** Time only, e.g. "14:05". */
export const TIME_FORMAT = 'HH:mm';
/** Time with seconds, used for "last updated" labels. */
export const TIME_WITH_SECONDS_FORMAT = 'HH:mm:ss';
/** Sortable full timestamp for technical views (logs, conversation overview). */
export const ISO_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

type DateInput = string | number | Date | Dayjs | null | undefined;

/**
 * Formats a date-like value with the given dayjs pattern; missing or invalid input gives "—".
 */
export function formatDateTime(value: DateInput, format: string = DATE_TIME_FORMAT): string {
  if (value == null || value === '') return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : '—';
}

/** Date only ({@link DATE_FORMAT}); missing or invalid input gives "—". */
export function formatDate(value: DateInput): string {
  return formatDateTime(value, DATE_FORMAT);
}

/** Full sortable timestamp ({@link ISO_DATE_TIME_FORMAT}); missing or invalid input gives "—". */
export function formatWhen(value: DateInput): string {
  return formatDateTime(value, ISO_DATE_TIME_FORMAT);
}

/** Split of a duration into whole units, largest first. */
interface DurationParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Splits a millisecond duration into whole days/hours/minutes/seconds (negative values clamp to 0).
 * @param largestUnit - Units above this one are folded into it (e.g. 'minute' gives 90 minutes, not 1h 30m).
 */
function splitDuration(durationMs: number, largestUnit: 'day' | 'hour' | 'minute'): DurationParts {
  let rest = Math.floor(Math.max(0, durationMs) / 1000);
  const days = largestUnit === 'day' ? Math.floor(rest / 86400) : 0;
  rest -= days * 86400;
  const hours = largestUnit === 'minute' ? 0 : Math.floor(rest / 3600);
  rest -= hours * 3600;
  const minutes = Math.floor(rest / 60);
  return { days, hours, minutes, seconds: rest - minutes * 60 };
}

/**
 * Untranslated duration styles:
 * - `compact`: "45s", "3m 05s" (job runtimes)
 * - `precise`: "850 ms", "1.7 s", "2 min 05 s" (flow executions, often sub-second)
 * - `clock`: "01:30:00" (a minutes-based duration as HH:mm:ss)
 */
export type DurationStyle = 'compact' | 'precise' | 'clock';

/**
 * Formats a millisecond duration in one of the {@link DurationStyle} styles.
 * Null or undefined (e.g. a job that is still running) gives "—".
 */
export function formatDuration(durationMs: number | null | undefined, style: DurationStyle): string {
  if (durationMs == null) return '—';

  if (style === 'precise') {
    if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
    const seconds = durationMs / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)} s`;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes} min ${String(rest).padStart(2, '0')} s`;
  }

  if (style === 'clock') {
    const { hours, minutes, seconds } = splitDuration(durationMs, 'hour');
    return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
  }

  const { minutes, seconds } = splitDuration(durationMs, 'minute');
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
}

/**
 * Formats a millisecond duration through translation keys under `keyPrefix`
 * (`seconds`, `minutesSeconds`, and depending on `largestUnit` also `hoursMinutes`, `daysHours`),
 * showing the two largest non-zero units, e.g. "2h 5m".
 */
export function formatDurationLabel(
  durationMs: number,
  t: TFunction,
  keyPrefix: string,
  largestUnit: 'day' | 'hour' | 'minute' = 'day'
): string {
  const { days, hours, minutes, seconds } = splitDuration(durationMs, largestUnit);
  if (days > 0) return t(`${keyPrefix}.daysHours`, { days, hours });
  if (hours > 0) return t(`${keyPrefix}.hoursMinutes`, { hours, minutes });
  if (minutes > 0) return t(`${keyPrefix}.minutesSeconds`, { minutes, seconds });
  return t(`${keyPrefix}.seconds`, { seconds });
}

/** Time presets shared by list filters. */
export type DateRangePreset = 'today' | '24h' | '7d' | '30d' | '90d';

/**
 * Resolves a time preset to a `[from, to]` range ending now; unknown presets give null.
 * @param wholeDays - Snap to calendar days (start of the first day to end of today)
 *   instead of a rolling window.
 */
export function getDateRangeFromPreset(preset: string, wholeDays = false): [Dayjs, Dayjs] | null {
  const now = dayjs();
  const days: Record<Exclude<DateRangePreset, 'today' | '24h'>, number> = { '7d': 7, '30d': 30, '90d': 90 };

  let from: Dayjs;
  if (preset === 'today') from = now.startOf('day');
  else if (preset === '24h') from = now.subtract(24, 'hour');
  else if (preset in days) from = now.subtract(days[preset as keyof typeof days], 'day');
  else return null;

  return wholeDays ? [from.startOf('day'), now.endOf('day')] : [from, now];
}
